import React, { useState, useEffect } from 'react'
import {
  XMarkIcon,
  BuildingOffice2Icon,
  UserIcon,
  PhoneIcon,
  EnvelopeIcon,
  GlobeAltIcon,
  PencilIcon,
  PlusIcon,
  StarIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  TagIcon,
  MapPinIcon,
  BriefcaseIcon,
  CurrencyDollarIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { api } from '../../services/authService'

const LeadDetailModal = ({ leadId, onClose, onEdit, onRefresh }) => {
  const [lead, setLead] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('info')
  const [showAddContact, setShowAddContact] = useState(false)
  const [savingContact, setSavingContact] = useState(false)
  const [newContact, setNewContact] = useState({
    name: '',
    role: '',
    email: '',
    phone: '',
    whatsapp: '',
    is_decision_maker: false
  })

  useEffect(() => {
    if (leadId) {
      loadLeadDetails()
    }
  }, [leadId])

  const loadLeadDetails = async () => {
    try {
      setLoading(true)
      const response = await api.get(`/api/marketing/leads/${leadId}`)
      setLead(response.data)
    } catch (error) {
      console.error('Erro ao carregar detalhes do lead:', error)
      toast.error('Erro ao carregar detalhes do lead')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('pt-BR')
  }

  const formatDateTime = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const getScoreBadgeClass = (score) => {
    const s = Number(score) || 0
    if (s >= 100) return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
    if (s >= 50) return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300'
    if (s > 0) return 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300'
    return 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
  }

  const handleAddContact = async (e) => {
    e.preventDefault()
    if (!newContact.name.trim()) {
      toast.error('Nome do contato e obrigatorio')
      return
    }
    try {
      setSavingContact(true)
      await api.post(`/api/marketing/leads/${leadId}/contacts`, newContact)
      toast.success('Contato adicionado com sucesso')
      setNewContact({ name: '', role: '', email: '', phone: '', whatsapp: '', is_decision_maker: false })
      setShowAddContact(false)
      loadLeadDetails()
      if (onRefresh) onRefresh()
    } catch (error) {
      console.error('Erro ao adicionar contato:', error)
      toast.error(error.response?.data?.error || 'Erro ao adicionar contato')
    } finally {
      setSavingContact(false)
    }
  }

  const getActivityIcon = (type) => {
    switch (type) {
      case 'email':
        return <EnvelopeIcon className="h-5 w-5 text-blue-500" />
      case 'whatsapp':
        return <ChatBubbleLeftRightIcon className="h-5 w-5 text-green-500" />
      default:
        return <ClockIcon className="h-5 w-5 text-gray-400" />
    }
  }

  const tabs = [
    { id: 'info', label: 'Informacoes', icon: BuildingOffice2Icon },
    { id: 'contacts', label: 'Contatos', icon: UserIcon },
    { id: 'opportunities', label: 'Oportunidades', icon: CurrencyDollarIcon },
    { id: 'activity', label: 'Atividade', icon: ClockIcon },
    { id: 'score', label: 'Score', icon: ChartBarIcon }
  ]

  if (!leadId) return null

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
      />

      {/* Side Panel */}
      <div className="absolute inset-y-0 right-0 w-full max-w-2xl bg-white dark:bg-gray-800 shadow-2xl flex flex-col">
        {/* Loading State */}
        {loading ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          </div>
        ) : !lead ? (
          <div className="flex-1 flex items-center justify-center">
            <p className="text-gray-500 dark:text-gray-400">Lead nao encontrado</p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-bold text-gray-900 dark:text-white truncate">
                    {lead.company_name}
                  </h2>
                  {lead.trade_name && (
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      {lead.trade_name}
                    </p>
                  )}
                  <div className="flex flex-wrap items-center gap-2 mt-2">
                    {lead.cnpj && (
                      <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                        {lead.cnpj}
                      </span>
                    )}
                    {lead.sector && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300">
                        <TagIcon className="h-3 w-3 mr-1" />
                        {lead.sector}
                      </span>
                    )}
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-bold ${getScoreBadgeClass(lead.score)}`}>
                      <StarIcon className="h-3 w-3 mr-1" />
                      Score: {lead.score || 0}
                    </span>
                    {lead.stage_name && (
                      <span
                        className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white"
                        style={{ backgroundColor: lead.stage_color || '#6b7280' }}
                      >
                        {lead.stage_name}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => onEdit && onEdit(lead)}
                    className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 dark:hover:text-blue-400 rounded-lg transition-colors"
                    title="Editar lead"
                  >
                    <PencilIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={onClose}
                    className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 dark:hover:text-gray-300 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-1 mt-4 -mb-4 overflow-x-auto">
                {tabs.map((tab) => {
                  const Icon = tab.icon
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-t-lg whitespace-nowrap transition-colors ${
                        activeTab === tab.id
                          ? 'bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 border border-b-0 border-gray-200 dark:border-gray-700'
                          : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {tab.label}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Info Tab */}
              {activeTab === 'info' && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <InfoField
                      icon={<BuildingOffice2Icon className="h-5 w-5" />}
                      label="Porte da Empresa"
                      value={lead.company_size || 'Nao informado'}
                    />
                    <InfoField
                      icon={<MapPinIcon className="h-5 w-5" />}
                      label="Cidade / Estado"
                      value={lead.city || lead.state ? `${lead.city || ''}${lead.city && lead.state ? ' - ' : ''}${lead.state || ''}` : 'Nao informado'}
                    />
                    <InfoField
                      icon={<GlobeAltIcon className="h-5 w-5" />}
                      label="Website"
                      value={lead.website || 'Nao informado'}
                      isLink={!!lead.website}
                    />
                    <InfoField
                      icon={<BriefcaseIcon className="h-5 w-5" />}
                      label="Origem do Lead"
                      value={lead.lead_source || 'Nao informado'}
                    />
                    <InfoField
                      icon={<ClockIcon className="h-5 w-5" />}
                      label="Criado em"
                      value={formatDateTime(lead.created_at)}
                    />
                    <InfoField
                      icon={<StarIcon className="h-5 w-5" />}
                      label="Score"
                      value={`${lead.score || 0} pontos`}
                    />
                  </div>

                  {lead.notes && (
                    <div className="mt-4">
                      <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Observacoes</h4>
                      <div className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">
                        {lead.notes}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Contacts Tab */}
              {activeTab === 'contacts' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Contatos ({lead.contacts?.length || 0})
                    </h3>
                    <button
                      onClick={() => setShowAddContact(!showAddContact)}
                      className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                    >
                      <PlusIcon className="h-4 w-4" />
                      Adicionar Contato
                    </button>
                  </div>

                  {/* Add Contact Form */}
                  {showAddContact && (
                    <form onSubmit={handleAddContact} className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 space-y-3 border border-blue-200 dark:border-blue-800">
                      <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-300">Novo Contato</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input
                          type="text"
                          placeholder="Nome *"
                          value={newContact.name}
                          onChange={(e) => setNewContact({ ...newContact, name: e.target.value })}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          required
                        />
                        <input
                          type="text"
                          placeholder="Cargo"
                          value={newContact.role}
                          onChange={(e) => setNewContact({ ...newContact, role: e.target.value })}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <input
                          type="email"
                          placeholder="E-mail"
                          value={newContact.email}
                          onChange={(e) => setNewContact({ ...newContact, email: e.target.value })}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <input
                          type="text"
                          placeholder="Telefone"
                          value={newContact.phone}
                          onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <input
                          type="text"
                          placeholder="WhatsApp"
                          value={newContact.whatsapp}
                          onChange={(e) => setNewContact({ ...newContact, whatsapp: e.target.value })}
                          className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        />
                        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <input
                            type="checkbox"
                            checked={newContact.is_decision_maker}
                            onChange={(e) => setNewContact({ ...newContact, is_decision_maker: e.target.checked })}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                          Decisor
                        </label>
                      </div>
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => setShowAddContact(false)}
                          className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        >
                          Cancelar
                        </button>
                        <button
                          type="submit"
                          disabled={savingContact}
                          className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {savingContact ? 'Salvando...' : 'Salvar'}
                        </button>
                      </div>
                    </form>
                  )}

                  {/* Contact List */}
                  {lead.contacts?.length > 0 ? (
                    <div className="space-y-3">
                      {lead.contacts.map((contact, index) => (
                        <div
                          key={contact.id || index}
                          className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">
                                  {contact.name}
                                </h4>
                                {contact.is_decision_maker && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">
                                    Decisor
                                  </span>
                                )}
                                {contact.is_primary && (
                                  <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">
                                    Principal
                                  </span>
                                )}
                              </div>
                              {contact.role && (
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{contact.role}</p>
                              )}
                            </div>
                          </div>
                          <div className="flex flex-wrap gap-4 mt-2 text-sm text-gray-600 dark:text-gray-400">
                            {contact.email && (
                              <span className="flex items-center gap-1">
                                <EnvelopeIcon className="h-4 w-4" />
                                {contact.email}
                              </span>
                            )}
                            {contact.phone && (
                              <span className="flex items-center gap-1">
                                <PhoneIcon className="h-4 w-4" />
                                {contact.phone}
                              </span>
                            )}
                            {contact.whatsapp && (
                              <span className="flex items-center gap-1">
                                <ChatBubbleLeftRightIcon className="h-4 w-4 text-green-500" />
                                {contact.whatsapp}
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <UserIcon className="h-10 w-10 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">Nenhum contato cadastrado</p>
                    </div>
                  )}
                </div>
              )}

              {/* Opportunities Tab */}
              {activeTab === 'opportunities' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Oportunidades ({lead.opportunities?.length || 0})
                  </h3>
                  {lead.opportunities?.length > 0 ? (
                    <div className="space-y-3">
                      {lead.opportunities.map((opp, index) => (
                        <div
                          key={opp.id || index}
                          className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1 min-w-0">
                              <h4 className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                                {opp.title}
                              </h4>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                Criada em {formatDate(opp.created_at)}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 ml-4">
                              {opp.stage_name && (
                                <span
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium text-white"
                                  style={{ backgroundColor: opp.stage_color || '#6b7280' }}
                                >
                                  {opp.stage_name}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="mt-2">
                            <span className="text-lg font-bold text-green-600 dark:text-green-400">
                              {formatCurrency(opp.monthly_value)}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">/mes</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <CurrencyDollarIcon className="h-10 w-10 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">Nenhuma oportunidade vinculada</p>
                    </div>
                  )}
                </div>
              )}

              {/* Activity Tab */}
              {activeTab === 'activity' && (
                <div className="space-y-4">
                  <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                    Atividade Recente
                  </h3>
                  {lead.recent_activity?.length > 0 ? (
                    <div className="relative">
                      {/* Timeline line */}
                      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700" />
                      <div className="space-y-4">
                        {lead.recent_activity.map((activity, index) => (
                          <div key={activity.id || index} className="relative flex items-start gap-4 pl-2">
                            <div className="relative z-10 flex items-center justify-center w-8 h-8 rounded-full bg-white dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700 shrink-0">
                              {getActivityIcon(activity.type)}
                            </div>
                            <div className="flex-1 min-w-0 pb-4">
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {activity.title}
                                </p>
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ml-2 shrink-0 ${
                                  activity.status === 'sent' || activity.status === 'delivered'
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                                    : activity.status === 'failed'
                                    ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300'
                                    : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                                }`}>
                                  {activity.status === 'sent' ? 'Enviado' :
                                   activity.status === 'delivered' ? 'Entregue' :
                                   activity.status === 'failed' ? 'Falhou' :
                                   activity.status === 'opened' ? 'Aberto' :
                                   activity.status === 'clicked' ? 'Clicado' :
                                   activity.status || 'Pendente'}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                {formatDateTime(activity.created_at)}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <ClockIcon className="h-10 w-10 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">Nenhuma atividade registrada</p>
                    </div>
                  )}
                </div>
              )}

              {/* Score Tab */}
              {activeTab === 'score' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                      Historico de Score
                    </h3>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-bold ${getScoreBadgeClass(lead.score)}`}>
                      {lead.score || 0} pts
                    </span>
                  </div>

                  {lead.score_history?.length > 0 ? (
                    <div className="space-y-2">
                      {lead.score_history.map((entry, index) => (
                        <div
                          key={entry.id || index}
                          className="flex items-center gap-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg px-4 py-3 border border-gray-200 dark:border-gray-700"
                        >
                          <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold shrink-0 ${
                            Number(entry.points) > 0
                              ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400'
                              : Number(entry.points) < 0
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400'
                              : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                          }`}>
                            {Number(entry.points) > 0 ? `+${entry.points}` : entry.points}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {entry.action}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {formatDateTime(entry.created_at)}
                            </p>
                          </div>
                          <div className="text-right shrink-0">
                            <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                              {entry.new_score} pts
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <ChartBarIcon className="h-10 w-10 mx-auto mb-2 opacity-40" />
                      <p className="text-sm">Nenhum historico de score</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  )
}

// Reusable info field component
const InfoField = ({ icon, label, value, isLink }) => (
  <div className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg">
    <div className="text-gray-400 dark:text-gray-500 mt-0.5 shrink-0">
      {icon}
    </div>
    <div className="min-w-0">
      <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
      {isLink ? (
        <a
          href={value.startsWith('http') ? value : `https://${value}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline truncate block"
        >
          {value}
        </a>
      ) : (
        <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{value}</p>
      )}
    </div>
  </div>
)

export default LeadDetailModal
